import type { RefObject } from "react";
import { useEffect, useRef } from "react";
import type { FormValues } from "@/widgets/classic-form/form";
import { useDataModelContext } from "@/entities/data-model";
import { useMetadataQuery } from "@/entities/form-metadata";
import type { FormAPI } from "@/shared/forms";
import { useEffectEvent } from "@/shared/hooks/useEffectEvent";

/**
 * Resets the form when upstream host-pushed inputs change (data model or form metadata).
 *
 * User-preferences changes are intentionally excluded from the trigger because a user's
 * own preference update (e.g. toggling exactness) must never clobber in-progress edits.
 * The reset target is still the full preferences-folded `defaultFormValues`, read fresh
 * at reset time via `useEffectEvent`.
 */
export function useResetFormOnUpstreamChange(
    formRef: RefObject<FormAPI<FormValues, any> | null>,
    defaultFormValues: FormValues,
) {
    const dataModel = useDataModelContext();
    const { data: formMetadata } = useMetadataQuery();

    const prevDataModelRef = useRef(dataModel);
    const prevMetadataRef = useRef(formMetadata);

    const resetFormValues = useEffectEvent(() => {
        formRef.current?.reset(defaultFormValues);
    });

    useEffect(() => {
        const dataModelChanged = prevDataModelRef.current !== dataModel;
        const metadataChanged = prevMetadataRef.current !== formMetadata;

        if (dataModelChanged || metadataChanged) {
            resetFormValues();
        }

        prevDataModelRef.current = dataModel;
        prevMetadataRef.current = formMetadata;
    }, [dataModel, formMetadata]);
}
